import { Instance, Modifier } from '@popperjs/core';

import { Utils } from '../utils';

// ================================================================ //
// -- Utilities

/**
 * Toggles PopperJS instance's event listeners on/off.
 * @param instance - PopperJS instance object
 * @param enabled - true to turn listeners on, false to turn off
 */
export const togglePopperListeners = (
  instance: Instance,
  enabled: boolean
): void => {
  if (!instance) return;

  instance.setOptions((options) => ({
    ...options,
    modifiers: [...options.modifiers, { name: 'eventListeners', enabled }],
  }));
};

/**
 * Checks all parents of "host" element for PopperJS attribute.
 * Returns true if a parent PopperJS element exists, false if not.
 * @param host - child element
 */
export const hasPopperAncestor = (host: HTMLElement): boolean => {
  return !!Utils.closestAncestor(host, '[data-popper-placement]');
};

// ================================================================ //
// -- Modifiers

/**
 * Custom PopperJS modifier that sets the "popper" element (e.g. Popover
 * message, dropdown menu, etc.) to the same width as the "reference"
 * element (e.g. "trigger" Button, <input> field, etc.). Can be configured
 * to set "min-width" on "popper" element (instead of "width") as well.
 * @param enabled - whether the modifier is functional or not
 * @param setMinWidth - sets "min-width" on "popper" element instead of "width"
 */
export const matchWidthModifier = (
  enabled: boolean,
  setMinWidth = false
): Modifier<string, Record<string, unknown>> => ({
  name: 'matchWidth',
  enabled,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper[
      setMinWidth ? 'minWidth' : 'width'
    ] = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style[setMinWidth ? 'minWidth' : 'width'] = `${
      (state.elements.reference as HTMLElement).offsetWidth
    }px`;
  },
});
